/**
 * Client-side musical chord transposer for instant (0ms) and offline chord shifting.
 */

const SHARP_SCALE = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const FLAT_SCALE  = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];

const NOTE_TO_INDEX = {
    'C': 0, 'B#': 0,
    'C#': 1, 'DB': 1, 'Db': 1,
    'D': 2,
    'D#': 3, 'EB': 3, 'Eb': 3,
    'E': 4, 'FB': 4, 'Fb': 4,
    'F': 5, 'E#': 5,
    'F#': 6, 'GB': 6, 'Gb': 6,
    'G': 7,
    'G#': 8, 'AB': 8, 'Ab': 8,
    'A': 9,
    'A#': 10, 'BB': 10, 'Bb': 10,
    'B': 11, 'CB': 11, 'Cb': 11
};

// Regex to capture root note, chord descriptor/suffix, and optional slash bass note
const CHORD_REGEX = /^([A-Ga-g][#b♭]?)(.*?)(\/([A-Ga-g][#b♭]?))?$/;

/**
 * Transpose a single note name by a number of semitones.
 * @param {string} note - e.g. "C", "F#", "Bb"
 * @param {number} semitones - semitone delta (positive or negative)
 * @param {boolean} preferFlats - whether to prefer flat notation
 * @returns {string} transposed note name
 */
export function transposeNote(note, semitones, preferFlats = false) {
    if (!note || semitones === 0) return note;
    
    // Normalize accidentals (convert Unicode ♭ to b)
    const normalized = note.replace(/♭/g, 'b');
    const formatted = normalized.charAt(0).toUpperCase() + normalized.slice(1);
    
    if (!(formatted in NOTE_TO_INDEX)) {
        return note;
    }
    
    const currentIdx = NOTE_TO_INDEX[formatted];
    const newIdx = (currentIdx + (semitones % 12) + 12) % 12;
    const scale = preferFlats ? FLAT_SCALE : SHARP_SCALE;
    return scale[newIdx];
}

/**
 * Transpose a full chord string (e.g. "C#m7/G#", "Bb7M", "F#dim", "G4/7")
 * @param {string} chord - chord string
 * @param {number} semitones - semitone delta
 * @returns {string} transposed chord string
 */
export function transposeChord(chord, semitones) {
    if (!chord || typeof chord !== 'string' || semitones === 0) {
        return chord;
    }

    const trimmed = chord.trim();
    if (!trimmed) return chord;

    const match = trimmed.match(CHORD_REGEX);
    if (!match) {
        return chord;
    }

    const rootNote = match[1];
    const suffix = match[2] || '';
    const bassSlash = match[3];
    const bassNote = match[4];

    // Determine if original chord used flats
    const hasFlats = rootNote.includes('b') || rootNote.includes('♭') || (bassNote && (bassNote.includes('b') || bassNote.includes('♭')));

    const newRoot = transposeNote(rootNote, semitones, hasFlats);
    let newBass = '';
    if (bassNote) {
        newBass = '/' + transposeNote(bassNote, semitones, hasFlats);
    } else if (bassSlash) {
        newBass = bassSlash;
    }

    return `${newRoot}${suffix}${newBass}`;
}

/**
 * Transpose a string that may hold MULTIPLE space-separated chords
 * (e.g. chord-item content like "B F#/Bb G#m" or "G D/F# Em  Em/D  G").
 * Each token is transposed individually, whitespace preserved.
 * Without this, only the first token was transposed and the rest stayed
 * (viewer showing "C F#/Bb G#m" instead of "C G/B Am").
 */
export function transposeChordTokens(text, semitones) {
    if (!text || typeof text !== 'string' || semitones === 0) {
        return text;
    }
    return text.split(/(\s+)/).map(part => {
        if (!part || /^\s+$/.test(part)) return part;
        return transposeChord(part, semitones);
    }).join('');
}

/**
 * Transpose all chords within an HTML string.
 * Supports Triad span structures:
 * - <span class="chord-item"[^>]*>...</span>
 * - <span class="chord-span"[^>]*>...</span>
 * - data-chord="..."
 * - <b data-chord-name="...">...</b>
 * - [Chord] bracketed notation
 * 
 * @param {string} html - HTML string containing lyrics and chords
 * @param {number} semitoneDelta - semitone delta
 * @returns {string} transposed HTML string
 */
export function transposeHtmlChords(html, semitoneDelta) {
    if (!html || typeof html !== 'string' || semitoneDelta === 0) {
        return html;
    }

    let result = html;

    // 1. Transpose <span class="chord-item"[^>]*>CHORD</span>
    // Inner text and data-chord may hold MULTIPLE space-separated chords ("B F#/Bb G#m")
    result = result.replace(/<span([^>]*class="[^"]*chord-item[^"]*"[^>]*)>([\s\S]*?)<\/span>/gi, (match, attrs, chordContent) => {
        const cleanContent = chordContent.trim();
        if (!cleanContent) return match;
        const newChord = transposeChordTokens(cleanContent, semitoneDelta);
        // Also update data-chord if present in attributes
        let updatedAttrs = attrs;
        if (attrs.includes('data-chord=')) {
            updatedAttrs = attrs.replace(/data-chord="([^"]*)"/gi, (m, oldChord) => {
                return `data-chord="${transposeChordTokens(oldChord, semitoneDelta)}"`;
            });
        }
        return `<span${updatedAttrs}>${newChord}</span>`;
    });

    // 2. Transpose <span class="chord-span"[^>]*>CHORD</span>
    result = result.replace(/<span([^>]*class="[^"]*chord-span[^"]*"[^>]*)>([\s\S]*?)<\/span>/gi, (match, attrs, chordContent) => {
        const cleanContent = chordContent.trim();
        if (!cleanContent) return match;
        const newChord = transposeChordTokens(cleanContent, semitoneDelta);
        let updatedAttrs = attrs;
        if (attrs.includes('data-chord=')) {
            updatedAttrs = attrs.replace(/data-chord="([^"]*)"/gi, (m, oldChord) => {
                return `data-chord="${transposeChordTokens(oldChord, semitoneDelta)}"`;
            });
        }
        return `<span${updatedAttrs}>${newChord}</span>`;
    });

    // 3. Transpose <b data-chord-name="..."[^>]*>CHORD</b>
    result = result.replace(/<b([^>]*data-chord-name="([^"]*)"[^>]*)>([\s\S]*?)<\/b>/gi, (match, attrs, chordAttr, innerText) => {
        const newChordAttr = transposeChordTokens(chordAttr, semitoneDelta);
        const newInnerText = transposeChordTokens(innerText.trim(), semitoneDelta);
        const updatedAttrs = attrs.replace(`data-chord-name="${chordAttr}"`, `data-chord-name="${newChordAttr}"`);
        return `<b${updatedAttrs}>${newInnerText}</b>`;
    });

    // 4. Transpose custom data-chord on sup/span elements without inner text replaced yet
    result = result.replace(/(<sup[^>]*data-type="chord"[^>]*data-chord=")([^"]+)("[^>]*>)/gi, (match, prefix, chord, suffix) => {
        return `${prefix}${transposeChord(chord, semitoneDelta)}${suffix}`;
    });

    // 5. Transpose bracketed chords in raw text format e.g. [C#m7]
    result = result.replace(/\[([A-Ga-g][#b♭]?[^\]]*)\]/g, (match, chord) => {
        return `[${transposeChord(chord, semitoneDelta)}]`;
    });

    return result;
}

export default {
    transposeNote,
    transposeChord,
    transposeChordTokens,
    transposeHtmlChords
};
